import { useRef, useState, useCallback, useEffect, type KeyboardEvent } from "react";
import type { Task, TaskId } from "../../../shared/types";
import { useSwipe } from "../../../shared/hooks/use-swipe";
import { useLongPress } from "../../../shared/hooks/use-long-press";
import { useDebouncedTaskUpdate } from "../components/task-item/useDebouncedTaskUpdate";

export function useTaskItemState(
  task: Task,
  onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void,
) {
  const [isEditing, setIsEditing] = useState(false);
  const [overrideValue, setOverrideValue] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [contextMenu, setContextMenu] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Debouncing pour le titre - 800ms pour éviter les requêtes multiples
  const { update: debouncedUpdate, flush: flushTitleUpdate } = useDebouncedTaskUpdate(
    task.id,
    onUpdate,
    800,
  );

  const swipe = useSwipe({
    onSwipeLeft: () => setConfirmDelete(true),
    onSwipeRight: () => {},
    threshold: 80,
  });

  const longPress = useLongPress({
    onLongPress: (position) => setContextMenu(position),
    duration: 500,
  });

  const isDone = task.status === "done";
  // editValue = override pendant l'édition, sinon task.title du serveur
  const editValue = overrideValue ?? task.title;
  const setEditValue = setOverrideValue;

  function startEdit() {
    setOverrideValue(task.title);
    setIsEditing(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  // Fermer l'édition après un court délai (pour laisser le temps au debounce d'envoyer)
  const closeEdit = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsEditing(false);
      setOverrideValue(null);
    }, 900); // Légèrement plus long que le délai de debounce (800ms)
  }, []);

  const commitEdit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== task.title) {
      debouncedUpdate({ title: trimmed });
    }
    closeEdit();
  }, [editValue, task.title, debouncedUpdate, closeEdit]);

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter") {
      // Flush immédiat sur Enter et fermer l'édition
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      flushTitleUpdate();
      setIsEditing(false);
      setOverrideValue(null);
    }
    if (event.key === "Escape") {
      // Annuler les changements en cours
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      setIsEditing(false);
      setOverrideValue(null);
    }
  }

  // Cleanup du timeout au démontage
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    isExpanded,
    setIsExpanded,
    confirmDelete,
    setConfirmDelete,
    contextMenu,
    setContextMenu,
    inputRef,
    swipe,
    longPress,
    isDone,
    startEdit,
    commitEdit,
    handleKeyDown,
  };
}
